import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Card, Grid, Skeleton, Typography } from '@mui/material';
import { AccountBalance } from '@mui/icons-material';
import axios from '../utils/axios';
import { useStatistics } from '../hooks/useStatistics';
import StatCard from '../design/StatCard';
import PnlText from '../design/PnlText';
import UpdatedStamp from '../design/UpdatedStamp';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { formatCurrencyInt } from '../design/format';

interface Portfolio {
  Id: number;
  Cash: number;
  Deposits: number;
  ProfitAndLoss: number;
  TraderId: number;
  NetLiquidAssets: number;
}

interface Position {
  Id: number;
  Ticker: string;
  Investment: number;
  CurrentValue: number;
}

interface PortfolioOverviewProps {
  traderId?: number;
}

const PortfolioOverview: React.FC<PortfolioOverviewProps> = ({ traderId = 1 }) => {
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const fetchSeq = useRef(0);

  const { kpis, refetch: refetchKpis } = useStatistics({ traderId });

  const fetchAll = useCallback(async (silent = false) => {
    const seq = ++fetchSeq.current;
    if (!silent) setLoading(true);
    try {
      const [portfolioRes, positionsRes] = await Promise.all([
        axios.get(`/portfolio?trader_id=${traderId}`).catch((err: any) => {
          if (err.response?.status === 404) return { data: null };
          throw err;
        }),
        axios.get(`/portfolio/${traderId}/positions`),
      ]);
      if (seq !== fetchSeq.current) return;  // stale response; a newer request is in flight
      setPortfolio(portfolioRes.data);
      setPositions(positionsRes.data);
      setUpdatedAt(new Date());
      setError(null);
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      console.error('Error fetching portfolio data:', err);
      if (!silent) setError('Failed to fetch portfolio data');
    } finally {
      if (seq === fetchSeq.current && !silent) setLoading(false);
    }
  }, [traderId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  // refetchKpis bypasses useStatistics' cache, so realized P&L also refreshes each poll.
  useAutoRefresh(() => { fetchAll(true); refetchKpis(); });

  const invested = positions.reduce((sum, p) => sum + p.Investment, 0);
  const currentValue = positions.reduce((sum, p) => sum + p.CurrentValue, 0);
  const unrealized = currentValue - invested;

  if (loading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Grid item xs={6} sm={4} md={2.4} key={i}>
            <Skeleton variant="rounded" height={96} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!portfolio) {
    return (
      <Card sx={{ textAlign: 'center', p: 4 }}>
        <AccountBalance sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">No current portfolio</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This trader doesn't have a portfolio yet.
        </Typography>
      </Card>
    );
  }

  // P&L as a share of what was deposited. Undefined (no subtitle) when there are
  // no deposits to measure against.
  const percentOfDeposits = (value: number): React.ReactNode =>
    portfolio.Deposits > 0
      ? <PnlText value={(value / portfolio.Deposits) * 100} format="percent" variant="body2" />
      : undefined;

  return (
    <Box sx={{ width: '100%' }}>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <UpdatedStamp updatedAt={updatedAt} />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            title="Net Liquid Assets"
            value={formatCurrencyInt(portfolio.NetLiquidAssets)}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard title="Deposits" value={formatCurrencyInt(portfolio.Deposits)} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard title="Cash" value={formatCurrencyInt(portfolio.Cash)} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            title="Realized P&L"
            value={<PnlText value={kpis ? kpis.total_pnl : null} format="currency" variant="h5" />}
            subtitle={kpis ? percentOfDeposits(kpis.total_pnl) : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard
            title="Unrealized P&L"
            value={<PnlText value={unrealized} format="currency" variant="h5" />}
            subtitle={percentOfDeposits(unrealized)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PortfolioOverview;
